import type { ConvertedCourseData } from "@/features/client-config/types/client-config";

/**
 * Convert Course Data Utility
 *
 * Converts SCS coordinates from system config to GPS coordinates
 * This conversion is done once when fetching system config and cached
 */
import type { SystemConfig } from "../types/system-config";
import {
  scsToWorld,
  type ScsPoint,
  type WorldCoordinatesConfig,
} from "./coordinate-transformer";
import { filterLaunchAreas } from "./filter-launch-areas";

/**
 * Convert system config course data (launch areas, targets) to GPS coordinates
 *
 * This function processes the radar system config and converts all SCS coordinates
 * to GPS coordinates for immediate use throughout the app.
 *
 * @param systemConfig - Radar system configuration (includes WorldCoordinates for transformation)
 * @param selectedLaunchAreaIds - Launch area ids to include; undefined includes all
 * @returns Converted course data with GPS coordinates
 */
export function convertCourseData(
  systemConfig: SystemConfig,
  selectedLaunchAreaIds?: string[]
): ConvertedCourseData | null {
  try {
    const worldCoordinates = systemConfig.Tracking.Geometry.WorldCoordinates;
    const launchAreas = filterLaunchAreas(
      systemConfig.Tracking.Geometry.Environment.LaunchAreas,
      selectedLaunchAreaIds
    );
    const targets = systemConfig.Tracking.Geometry.Environment.Targets;

    // Validate WorldCoordinates has required fields
    if (
      worldCoordinates.Origin.Latitude === null ||
      worldCoordinates.Origin.Longitude === null ||
      worldCoordinates.Origin.Altitude === null ||
      worldCoordinates.Reference.Latitude === null ||
      worldCoordinates.Reference.Longitude === null
    ) {
      console.error(
        "❌ [Convert] WorldCoordinates Origin/Reference has null values"
      );
      return null;
    }

    const worldCoords: WorldCoordinatesConfig = {
      Origin: {
        Latitude: worldCoordinates.Origin.Latitude,
        Longitude: worldCoordinates.Origin.Longitude,
        Altitude: worldCoordinates.Origin.Altitude,
      },
      Reference: {
        Latitude: worldCoordinates.Reference.Latitude,
        Longitude: worldCoordinates.Reference.Longitude,
      },
    };

    console.log("🔄 [Convert] Converting course data from SCS to GPS...");

    // Convert all launch areas (tee boxes)
    const teeBoxes = launchAreas.map((launchArea) => {
      // Convert all vertices from SCS to GPS
      const verticesGps: [number, number][] = launchArea.Vertices.map(
        (vertexScs) => {
          const scsPoint: ScsPoint = vertexScs as ScsPoint;
          const gpsPoint = scsToWorld(scsPoint, worldCoords);
          return [gpsPoint.lon, gpsPoint.lat];
        }
      );

      // Calculate center from SCS vertices
      const vertices = launchArea.Vertices;
      const sumX = vertices.reduce((sum, v) => sum + v[0], 0);
      const sumY = vertices.reduce((sum, v) => sum + v[1], 0);
      const sumZ = vertices.reduce((sum, v) => sum + v[2], 0);
      const centerScs: ScsPoint = [
        sumX / vertices.length,
        sumY / vertices.length,
        sumZ / vertices.length,
      ];

      const centerGps = scsToWorld(centerScs, worldCoords);
      const position: [number, number] = [centerGps.lon, centerGps.lat];

      console.log(`  🏌️ Tee box: ${launchArea.Name}`, {
        centerGps: position,
        verticesCount: verticesGps.length,
      });

      return {
        id: launchArea.Id,
        name: launchArea.Name,
        position,
        vertices: verticesGps,
        verticesScs: launchArea.Vertices,
      };
    });

    // Find hole/pin target
    const pinTarget = targets.find(
      (t) => t.Name.toLowerCase() === "pin" || t.Name.toLowerCase() === "hole"
    );

    let hole: ConvertedCourseData["hole"] | undefined;
    if (pinTarget) {
      const pinScs: ScsPoint = pinTarget.Position as ScsPoint;
      const pinGps = scsToWorld(pinScs, worldCoords);
      hole = {
        id: pinTarget.Id,
        name: pinTarget.Name,
        position: [pinGps.lon, pinGps.lat],
        positionScs: pinTarget.Position,
      };
      console.log(`  ⛳ Hole: ${pinTarget.Name}`, {
        gps: hole.position,
        scs: hole.positionScs,
      });
    }

    // Convert other targets (COF, etc.)
    const convertedTargets = targets
      .filter(
        (t) => t.Name.toLowerCase() !== "pin" && t.Name.toLowerCase() !== "hole"
      )
      .map((target) => {
        const targetScs: ScsPoint = target.Position as ScsPoint;
        const targetGps = scsToWorld(targetScs, worldCoords);
        const converted = {
          id: target.Id,
          name: target.Name,
          position: [targetGps.lon, targetGps.lat] as [number, number],
          positionScs: target.Position,
        };
        console.log(`  🎯 Target: ${target.Name}`, {
          gps: converted.position,
          scs: converted.positionScs,
        });
        return converted;
      });

    console.log("✅ [Convert] Course data converted successfully");

    return {
      teeBoxes,
      hole,
      targets: convertedTargets,
    };
  } catch (error) {
    console.error("❌ [Convert] Error converting course data:", error);
    return null;
  }
}
